// src/pages/abonnements/AbonnementsExpires.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert, Button, Badge, Table, Pagination, ProgressBar } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate, formatNumber } from '../../utils/formatters';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';

const COLORS = {
  primary: '#295AA7',  // Bleu foncé principal
  secondary: '#FDB52F', // Jaune/orange secondaire
  success: '#10b981',   // Vert
  warning: '#f59e0b',   // Orange
  danger: '#ef4444',    // Rouge
  info: '#3b82f6',      // Bleu clair
  purple: '#8b5cf6'     // Violet
};

// Dégradés avec vos couleurs
const GRADIENT_COLORS = ['#295AA7', '#1e3f7a', '#FDB52F', '#e5a229'];

export default function AbonnementsExpires() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [rawResponse, setRawResponse] = useState('');
  const [filter, setFilter] = useState('tous'); // 'tous', 'expires', 'bientot'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userStr = localStorage.getItem('user');
      
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const response = await fetch(`https://josephsbusiness.com/api/abonnements_expires.php?user_id=${userId}`, {
        headers: {
          'Accept': 'application/json'
        }
      });

      const textResponse = await response.text();
      setRawResponse(textResponse.substring(0, 1000));

      if (!textResponse || textResponse.trim() === '') {
        throw new Error("Le serveur a retourné une réponse vide");
      }

      let result;
      try {
        result = JSON.parse(textResponse);
        console.log("✅ Données reçues:", result);
      } catch (e) {
        throw new Error(`Erreur de parsing JSON: ${e.message}`);
      }

      if (result.success) {
        setData(result);
        setCurrentPage(1);
      } else {
        setError(result.message || 'Erreur de chargement');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour obtenir le statut et les jours restants
  const getExpiryStatus = (dateFin) => {
    const today = new Date();
    const fin = new Date(dateFin);
    const diffDays = Math.ceil((fin - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { 
        status: 'expired', 
        color: 'danger', 
        label: 'Expiré', 
        icon: 'ri-close-circle-line',
        days: Math.abs(diffDays)
      };
    } else if (diffDays <= 7) {
      return { 
        status: 'critical', 
        color: 'danger', 
        label: 'Expiration imminente', 
        icon: 'ri-alert-line',
        days: diffDays
      };
    } else if (diffDays <= 30) {
      return { 
        status: 'warning', 
        color: 'warning', 
        label: 'Expire bientôt', 
        icon: 'ri-time-line',
        days: diffDays
      };
    } else {
      return { 
        status: 'active', 
        color: 'success', 
        label: 'Actif', 
        icon: 'ri-checkbox-circle-line',
        days: diffDays
      };
    }
  };

  // Filtrer les données selon l'onglet sélectionné
  const getFilteredData = () => {
    if (!data) return { abonnements: [], total: 0 };
    
    let abonnements = [];
    let total = 0;
    
    if (filter === 'expires') {
      abonnements = data.abonnements_expires || [];
      total = data.stats?.total_expires || 0;
    } else if (filter === 'bientot') {
      abonnements = data.abonnements_bientot_expires || [];
      total = data.stats?.total_bientot_expires || 0;
    } else {
      // Fusionner les deux listes pour 'tous'
      abonnements = [
        ...(data.abonnements_bientot_expires || []),
        ...(data.abonnements_expires || [])
      ].sort((a, b) => new Date(a.date_fin) - new Date(b.date_fin));
      total = (data.stats?.total_expires || 0) + (data.stats?.total_bientot_expires || 0);
    }
    
    return { abonnements, total };
  };

  // Pagination
  const { abonnements: filteredAbonnements, total: filteredTotal } = getFilteredData();
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAbonnements.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAbonnements.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
      }
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  if (loading) {
    return (
      <>
         <Header />
                <div className="main main-app p-3 p-lg-4">
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                    <div className="text-center">
                      <div className="spinner-wrapper mb-4">
                        <Spinner animation="border" variant="primary" className="pulse-spinner" style={{ width: '4rem', height: '4rem' }} />
                      </div>
                      <h5 className="fw-bold text-primary">Chargement des abonnements expirés</h5>
                      <p className="text-muted">Récupération des statistiques en cours...</p>
                    </div>
                  </div>
                </div>
                <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="error-container text-center py-4">
            <div className="error-icon-wrapper mb-3">
              <i className="ri-error-warning-line text-danger" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5 className="fw-semibold text-danger mb-2">Oups ! Une erreur est survenue</h5>
            <p className="text-muted small mb-3">{error}</p>
            {rawResponse && (
              <div className="debug-box mb-3">
                <p className="text-muted small mb-2">Réponse du serveur :</p>
                <pre className="debug-pre">{rawResponse}</pre>
              </div>
            )}
            <div className="d-flex gap-2 justify-content-center">
              <Button variant="primary" size="sm" onClick={fetchData}>
                <i className="ri-refresh-line me-1"></i>
                Réessayer
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={() => navigate('/abonnements/liste')}>
                <i className="ri-arrow-left-line me-1"></i>
                Retour
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!data || !data.stats) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <div className="text-center py-4">
            <i className="ri-inbox-line fs-1 text-muted mb-3 d-block"></i>
            <h5 className="fw-semibold">Aucune donnée disponible</h5>
            <p className="text-muted small mb-3">Aucun abonnement trouvé</p>
            <Button variant="primary" size="sm" onClick={fetchData}>
              <i className="ri-refresh-line me-1"></i>
              Réessayer
            </Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const stats = data.stats;

  const statsCards = [
    { 
      label: 'Total abonnements', 
      value: stats.total_abonnements, 
      color: 'primary', 
      icon: 'ri-calendar-check-line',
      bg: 'primary-gradient'
    },
    { 
      label: 'Actifs', 
      value: stats.total_actifs, 
      color: 'success', 
      icon: 'ri-checkbox-circle-line',
      bg: 'success-gradient'
    },
    { 
      label: 'Expirés', 
      value: stats.total_expires, 
      color: 'danger', 
      icon: 'ri-close-circle-line',
      bg: 'danger-gradient'
    },
    { 
      label: 'Expire bientôt', 
      value: stats.total_bientot_expires, 
      color: 'warning', 
      icon: 'ri-alert-line',
      bg: 'warning-gradient'
    }
  ];

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4 dashboard-styled">
        <div className="container-fluid">
          {/* En-tête stylisé */}
          <Row className="mb-4 fade-in">
            <Col md={12}>
              <div className="d-flex align-items-center justify-content-between header-styled">
                <div className="d-flex align-items-center gap-3">
                  <div className="header-icon">
                    <i className="ri-calendar-check-line"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold mb-0 gradient-text">Abonnements expirés</h5>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <span className="badge-styled">
                        <i className="ri-bar-chart-line me-1"></i>
                        Suivi des échéances
                      </span>
                      <span className="badge-styled">
                        <i className="ri-calendar-line me-1"></i>
                        {new Date().toLocaleDateString('fr-FR', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    className="btn-outline-styled"
                    onClick={() => navigate('/abonnements/liste')}
                  >
                    <i className="ri-arrow-left-line me-1"></i>
                    Retour
                  </Button>
                  <Button 
                    variant="primary" 
                    size="sm"
                    className="btn-styled"
                    onClick={fetchData}
                  >
                    <i className="ri-refresh-line me-1"></i>
                    Actualiser
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Cartes statistiques */}
          <Row className="g-3 mb-4">
            {statsCards.map((stat, index) => (
              <Col md={3} key={index}>
                <Card className="stat-card-styled h-100">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-start justify-content-between mb-2">
                      <div className={`stat-icon-styled bg-${stat.bg}`}>
                        <i className={`${stat.icon} text-white`}></i>
                      </div>
                    </div>
                    <h6 className="stat-label">{stat.label}</h6>
                    <h4 className="stat-value" style={{ color: COLORS[stat.color] }}>
                      {formatNumber(stat.value)}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Montant total et progression */}
          <Row className="g-4 mb-4">
            <Col md={6}>
              <Card className="stats-card-styled h-100">
                <Card.Header className="stats-header">
                  <h6 className="fw-semibold mb-1">
                    <i className="ri-money-euro-circle-line me-2" style={{ color: COLORS.primary }}></i>
                    Montant total
                  </h6>
                  <small className="text-muted-soft">Valeur des abonnements</small>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <span className="display-6 fw-bold" style={{ color: COLORS.primary }}>
                        {formatCurrency(stats.montant_total || 0)}
                      </span>
                      <p className="text-muted-soft small mt-2">Tous abonnements confondus</p>
                    </div>
                    <div className="total-icon-wrapper">
                      <i className="ri-bank-line fs-1" style={{ color: COLORS.primary, opacity: 0.5 }}></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="stats-card-styled h-100">
                <Card.Header className="stats-header">
                  <h6 className="fw-semibold mb-1">
                    <i className="ri-pie-chart-line me-2" style={{ color: COLORS.secondary }}></i>
                    Répartition
                  </h6>
                  <small className="text-muted-soft">Statut des abonnements</small>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="small fw-semibold">Actifs</span>
                      <span className="small text-success">{stats.total_actifs || 0} abonnements</span>
                    </div>
                    <ProgressBar 
                      now={stats.total_abonnements ? (stats.total_actifs / stats.total_abonnements) * 100 : 0} 
                      variant="success" 
                      className="progress-styled"
                      style={{ height: '6px' }}
                    />
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="small fw-semibold">Expire bientôt</span>
                      <span className="small" style={{ color: COLORS.warning }}>{stats.total_bientot_expires || 0} abonnements</span>
                    </div>
                    <ProgressBar 
                      now={stats.total_abonnements ? (stats.total_bientot_expires / stats.total_abonnements) * 100 : 0} 
                      variant="warning" 
                      className="progress-styled"
                      style={{ height: '6px' }}
                    />
                  </div>
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="small fw-semibold">Expirés</span>
                      <span className="small text-danger">{stats.total_expires || 0} abonnements</span>
                    </div>
                    <ProgressBar 
                      now={stats.total_abonnements ? (stats.total_expires / stats.total_abonnements) * 100 : 0} 
                      variant="danger" 
                      className="progress-styled"
                      style={{ height: '6px' }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Filtres par onglets */}
          <Row className="mb-3">
            <Col md={12}>
              <div className="filters-container">
                <span 
                  className={`filter-chip ${filter === 'tous' ? 'active' : ''}`}
                  onClick={() => {
                    setFilter('tous');
                    setCurrentPage(1);
                  }}
                >
                  Tous
                  <span className="filter-count">{stats.total_expires + stats.total_bientot_expires}</span>
                </span>
                <span 
                  className={`filter-chip ${filter === 'expires' ? 'active' : ''}`}
                  onClick={() => {
                    setFilter('expires');
                    setCurrentPage(1);
                  }}
                >
                  Expirés
                  <span className="filter-count">{stats.total_expires}</span>
                </span>
                <span 
                  className={`filter-chip ${filter === 'bientot' ? 'active' : ''}`}
                  onClick={() => {
                    setFilter('bientot');
                    setCurrentPage(1);
                  }}
                >
                  Expire bientôt
                  <span className="filter-count">{stats.total_bientot_expires}</span>
                </span>
              </div>
            </Col>
          </Row>

          {/* Tableau des abonnements */}
          <Row className="g-4">
            <Col md={12}>
              <Card className="table-card-styled">
                <Card.Header className="table-header">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                      <h6 className="fw-semibold mb-1">
                        <i className="ri-calendar-line me-2" style={{ color: COLORS.primary }}></i>
                        Liste des abonnements
                      </h6>
                      <small className="text-muted-soft">
                        {filter === 'tous' ? 'Tous les abonnements' : 
                         filter === 'expires' ? 'Abonnements expirés' : 
                         'Abonnements expirant bientôt'}
                      </small>
                    </div>
                    <span className="total-badge">
                      {filteredTotal} abonnement(s)
                    </span>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle table-styled">
                      <thead>
                        <tr>
                          <th className="px-3 py-2">N° Abonnement</th>
                          <th className="px-3 py-2">Église</th>
                          <th className="px-3 py-2">Date début</th>
                          <th className="px-3 py-2">Date fin</th>
                          <th className="px-3 py-2 text-end">Montant</th>
                          <th className="px-3 py-2 text-center">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length > 0 ? (
                          currentItems.map((abonnement, index) => {
                            const status = getExpiryStatus(abonnement.date_fin);
                            return (
                              <tr key={index} className="table-row-styled">
                                <td className="px-3 py-2">
                                  <span className="matricule-badge">
                                    {abonnement.matri_abon || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <span className="fw-semibold">
                                    {abonnement.nom_eglise || abonnement.matri_eglise}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <span className="date-text">
                                    <i className="ri-calendar-line me-1"></i>
                                    {formatDate(abonnement.date_deb)}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <span className="date-text">
                                    <i className="ri-calendar-line me-1"></i>
                                    {formatDate(abonnement.date_fin)}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-end">
                                  <span className="fw-bold" style={{ color: COLORS.primary }}>
                                    {formatCurrency(abonnement.montant)}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <div className="d-flex flex-column align-items-center">
                                    <span className={`status-badge status-${status.status}`}>
                                      <i className={`${status.icon} me-1`}></i>
                                      {status.label}
                                    </span>
                                    {status.status !== 'active' && (
                                      <small className={`text-${status.color} mt-1`}>
                                        {status.status === 'expired' 
                                          ? `Expiré depuis ${status.days} jours`
                                          : `Dans ${status.days} jours`
                                        }
                                      </small>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-4 empty-state">
                              <i className="ri-inbox-line fs-1 mb-2 d-block"></i>
                              <span className="small">Aucun abonnement trouvé</span>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
                {totalPages > 1 && (
                  <Card.Footer className="table-footer">
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted-soft">
                        {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredAbonnements.length)} sur {filteredAbonnements.length}
                      </small>
                      <Pagination className="mb-0 pagination-styled" size="sm">
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                        {renderPagination()}
                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                      </Pagination>
                    </div>
                  </Card.Footer>
                )}
              </Card>
            </Col>
          </Row>

          {/* Récapitulatif */}
          <Row className="mt-4">
            <Col md={12}>
              <Card className="summary-card-styled">
                <Card.Body className="p-3">
                  <Row className="align-items-center">
                    <Col md={8}>
                      <div className="d-flex align-items-center">
                        <i className="ri-information-line fs-4 me-3" style={{ color: COLORS.info }}></i>
                        <div>
                          <span className="fw-semibold d-block">Information importante</span>
                          <small className="text-muted-soft">
                            Les abonnements expirés depuis plus de 30 jours seront automatiquement archivés. 
                            Pensez à renouveler les abonnements avant leur date d'expiration.
                          </small>
                        </div>
                      </div>
                    </Col>
                    <Col md={4} className="text-md-end mt-3 mt-md-0">
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="btn-outline-styled"
                        style={{ borderColor: COLORS.primary, color: COLORS.primary }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = COLORS.primary;
                          e.target.style.color = 'white';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = COLORS.primary;
                        }}
                      >
                        <i className="ri-mail-send-line me-1"></i>
                        Envoyer rappel
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        <style>{`
          .dashboard-styled {
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            min-height: 100vh;
          }

          .gradient-text {
            background: linear-gradient(135deg, #1e293b, #0f172a);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .header-styled {
            background: white;
            padding: 1rem 1.5rem;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.03);
            border: 1px solid rgba(0,0,0,0.02);
          }

          .header-icon {
            width: 48px;
            height: 48px;
            border-radius: 14px;
            background: linear-gradient(135deg, #295AA7, #1e3f7a);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 6px 12px rgba(41, 90, 167, 0.2);
          }

          .header-icon i {
            font-size: 24px;
            color: white;
          }

          .badge-styled {
            background: rgba(41, 90, 167, 0.08);
            color: #295AA7;
            padding: 0.35rem 0.75rem;
            border-radius: 30px;
            font-size: 0.75rem;
            font-weight: 500;
            display: inline-flex;
            align-items: center;
            border: 1px solid rgba(41, 90, 167, 0.1);
          }

          .btn-styled {
            background: linear-gradient(135deg, #295AA7, #1e3f7a);
            border: none;
            padding: 0.4rem 1.2rem;
            border-radius: 30px;
            font-size: 0.85rem;
            font-weight: 500;
            box-shadow: 0 4px 10px rgba(41, 90, 167, 0.2);
            transition: all 0.3s ease;
            color: white;
          }

          .btn-styled:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 16px rgba(41, 90, 167, 0.3);
          }

          .btn-outline-styled {
            border: 1px solid #e2e8f0;
            background: white;
            padding: 0.4rem 1.2rem;
            border-radius: 30px;
            font-size: 0.85rem;
            font-weight: 500;
            color: #64748b;
            transition: all 0.3s ease;
          }

          .btn-outline-styled:hover {
            border-color: #FDB52F;
            color: #FDB52F;
            background: #f8fafc;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(253, 181, 47, 0.15);
          }

          .stat-card-styled {
            border: none;
            border-radius: 16px;
            background: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(0,0,0,0.03);
            border: 1px solid rgba(0,0,0,0.02);
          }

          .stat-card-styled:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 30px -8px rgba(41, 90, 167, 0.15);
          }

          .stat-icon-styled {
            width: 42px;
            height: 42px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.2rem;
          }

          .bg-primary-gradient { 
            background: linear-gradient(135deg, #295AA7, #1e3f7a); 
          }

          .bg-success-gradient { 
            background: linear-gradient(135deg, #10b981, #059669); 
          }

          .bg-danger-gradient { 
            background: linear-gradient(135deg, #ef4444, #dc2626); 
          }

          .bg-warning-gradient { 
            background: linear-gradient(135deg, #f59e0b, #d97706); 
          }

          .stat-label {
            color: #64748b;
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            margin-bottom: 0.25rem;
          }

          .stat-value {
            font-size: 1.6rem;
            font-weight: 700;
            margin-bottom: 0;
          }

          .stats-card-styled {
            border: none;
            border-radius: 20px;
            background: white;
            box-shadow: 0 4px 20px rgba(0,0,0,0.03);
            border: 1px solid rgba(0,0,0,0.02);
          }

          .stats-header {
            background: transparent;
            border-bottom: 1px solid rgba(0,0,0,0.03);
            padding: 1rem 1.5rem;
          }

          .text-muted-soft {
            color: #94a3b8 !important;
          }

          .progress-styled {
            background-color: #e9ecef;
            border-radius: 10px;
          }

          .total-icon-wrapper {
            width: 60px;
            height: 60px;
            border-radius: 30px;
            background: rgba(41, 90, 167, 0.05);
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .filters-container {
            display: flex;
            gap: 0.5rem;
            background: white;
            padding: 0.5rem;
            border-radius: 40px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.02);
            border: 1px solid rgba(0,0,0,0.02);
            width: fit-content;
          }

          .filter-chip {
            padding: 0.4rem 1.2rem;
            border-radius: 30px;
            font-size: 0.8rem;
            font-weight: 500;
            color: #64748b;
            cursor: pointer;
            transition: all 0.2s ease;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }

          .filter-chip:hover {
            background: #f8fafc;
          }

          .filter-chip.active {
            background: linear-gradient(135deg, #295AA7, #1e3f7a);
            color: white;
            box-shadow: 0 4px 10px rgba(41, 90, 167, 0.2);
          }

          .filter-count {
            background: rgba(255,255,255,0.2);
            padding: 0.1rem 0.4rem;
            border-radius: 20px;
            font-size: 0.65rem;
          }

          .table-card-styled {
            border: none;
            border-radius: 20px;
            background: white;
            box-shadow: 0 4px 20px rgba(0,0,0,0.03);
            border: 1px solid rgba(0,0,0,0.02);
          }

          .table-header {
            background: transparent;
            border-bottom: 1px solid rgba(0,0,0,0.03);
            padding: 1rem 1.5rem;
          }

          .total-badge {
            background: linear-gradient(135deg, #295AA7, #1e3f7a);
            color: white;
            padding: 0.3rem 1rem;
            border-radius: 30px;
            font-size: 0.75rem;
            font-weight: 600;
            box-shadow: 0 4px 10px rgba(41, 90, 167, 0.2);
          }

          .table-styled {
            font-size: 0.85rem;
          }

          .table-styled thead th {
            background: #f8fafc;
            color: #64748b;
            font-weight: 600;
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            border-bottom: 2px solid #e2e8f0;
          }

          .table-row-styled {
            transition: all 0.2s ease;
          }

          .table-row-styled:hover {
            background: linear-gradient(135deg, #f8fafc, #ffffff);
           
            box-shadow: 0 2px 8px rgba(0,0,0,0.03);
          }

          .matricule-badge {
            background: #f1f5f9;
            color: #334155;
            padding: 0.2rem 0.6rem;
            border-radius: 30px;
            font-size: 0.7rem;
            font-weight: 500;
          }

          .date-text {
            color: #64748b;
            font-size: 0.75rem;
          }

          .status-badge {
            padding: 0.25rem 0.8rem;
            border-radius: 30px;
            font-size: 0.7rem;
            font-weight: 500;
            display: inline-flex;
            align-items: center;
          }

          .status-expired {
            background: rgba(239, 68, 68, 0.1);
            color: #dc2626;
          }

          .status-critical {
            background: rgba(239, 68, 68, 0.1);
            color: #dc2626;
          }

          .status-warning {
            background: rgba(245, 158, 11, 0.1);
            color: #d97706;
          }

          .status-active {
            background: rgba(16, 185, 129, 0.1);
            color: #059669;
          }

          .table-footer {
            background: transparent;
            border-top: 1px solid rgba(0,0,0,0.03);
            padding: 1rem 1.5rem;
          }

          .empty-state {
            color: #94a3b8;
            padding: 2rem;
          }

          .pagination-styled .page-link {
            border-radius: 8px;
            border: 1px solid #e9ecef;
            color: #495057;
            padding: 0.2rem 0.5rem;
            font-size: 0.75rem;
            transition: all 0.2s ease;
          }

          .pagination-styled .page-link:hover {
            background: #295AA7;
            color: white;
            border-color: #295AA7;
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(41, 90, 167, 0.2);
          }

          .pagination-styled .page-item.active .page-link {
            background: linear-gradient(135deg, #295AA7, #1e3f7a);
            border-color: #295AA7;
            color: white;
            box-shadow: 0 4px 10px rgba(41, 90, 167, 0.3);
          }

          .summary-card-styled {
            border: none;
            border-radius: 16px;
            background: linear-gradient(135deg, #ffffff, #f8fafc);
            border: 1px solid rgba(0,0,0,0.02);
          }

          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: calc(100vh - 130px);
            width: 100%;
          }

          .loading-wrapper {
            position: relative;
            max-width: 300px;
            padding: 1.5rem;
          }

          .spinner-glow {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(41, 90, 167, 0.15) 0%, transparent 70%);
            animation: pulse 2s ease-in-out infinite;
          }

          .main-spinner {
            width: 3rem;
            height: 3rem;
            border-width: 0.2rem;
            border-color: #295AA7 transparent #295AA7 transparent;
            position: relative;
            z-index: 2;
          }

          @keyframes pulse {
            0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.5; }
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .fade-in {
            animation: fadeIn 0.5s ease-out;
          }
        `}</style>

        <Footer />
      </div>
    </>
  );
}